import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import { useWaitingList } from "@ryde/redux";
import {
  editFormDataAF,
  IRideDto,
  isPastTaxiSupplierRide,
  RideCrudModeKeyTuple,
  useSystemParams,
} from "@ryde/shared";

import { RideWaitingListPanelStyled } from "./ride-tabs-waiting-list-panel.styled";
import { RideWaitingListRow } from "./ride-tabs-waiting-list-row";

export const RideTabsWaitingListPanel: FC = () => {
  const { t } = useTranslation();
  const { waitingListMode } = useSystemParams();
  const rideDto = useRecoilValue<IRideDto | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );

  const rideId = rideDto?.id;
  // The waiting-list feature is enabled whenever a mode is configured (BE renamed the
  // boolean `enableWaitingList` to the `waitingListMode` enum: waitingList | reminderList).
  // Intentionally course-agnostic: the BE allows joining the waiting list for any ride
  // (course and non-course alike), so the panel must show for non-course rides too.
  // RYDE-7633: a past taxi-supplier ride is locked — hide the waiting list so no passenger can be
  // invited/added to a ride that has already started.
  const isHidden =
    !waitingListMode || !rideId || isPastTaxiSupplierRide(rideDto);

  const { queue, sendInvitation } = useWaitingList({
    rideId,
    isEnabled: !isHidden,
  });

  if (isHidden || !queue) {
    return null;
  }

  const vacancy = queue?.vacancy ?? 0;
  const waitingCount = queue?.waitingCount ?? 0;
  const rows = queue?.all ?? [];

  return (
    <RideWaitingListPanelStyled>
      <div className="RideWaitingListPanel-Header">
        <span className="RideWaitingListPanel-HeaderIcon">
          <PIC.ClockSVG />
        </span>
        <span className="RideWaitingListPanel-HeaderTitle">
          {t("waitingList.title")}
        </span>

        <div className="RideWaitingListPanel-Vacancy">
          <span>{t("waitingList.vacancy")}</span>
          <span
            className={clsx(
              "RideWaitingListPanel-Chip",
              vacancy > 0
                ? "RideWaitingListPanel-Chip--green"
                : "RideWaitingListPanel-Chip--red",
            )}
          >
            <span className="RideWaitingListPanel-ChipValue">{vacancy}</span>
          </span>
        </div>

        <div className="RideWaitingListPanel-WaitingCount">
          <span>{t("waitingList.waitingPassengers")}</span>
          <span
            className={clsx(
              "RideWaitingListPanel-Chip",
              waitingCount > 0
                ? "RideWaitingListPanel-Chip--red"
                : "RideWaitingListPanel-Chip--green",
            )}
          >
            <span className="RideWaitingListPanel-ChipValue">
              {waitingCount}
            </span>
          </span>
        </div>
      </div>

      <div className="RideWaitingListPanel-Rows">
        {rows.length === 0 && (
          <div className="RideWaitingListPanel-Empty">
            {t("waitingList.empty")}
          </div>
        )}
        {rows.map(row => (
          <RideWaitingListRow
            key={row.id}
            row={row}
            onSendInvitation={sendInvitation}
            rideDto={rideDto}
          />
        ))}
      </div>
    </RideWaitingListPanelStyled>
  );
};
